import { zodResolver } from "@hookform/resolvers/zod";
import { PlusIcon } from "@heroicons/react/outline";
import { useForm } from "react-hook-form";
import { z } from "zod";

import SelectEmails from "../../CreateInviteForm/SelectEmails";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BasicTooltip } from "@/components/ui/Tooltip";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useAuth } from "@/context/auth-context";
import { useCreateWorkspace } from "@/queries";
import { useCreateWorkspaceInvite } from "@/queries/workspace";
import { useCurrentUser } from "@/queries/users";
import { useOrganizationSubscriptionData } from "@/providers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { ToastType } from "@/enums/toast";

interface WorkspaceModalProps {
  setOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  organizationId: number;
  hasTrigger?: boolean;
}

const createWorkspaceSchema = z.object({
  name: z.string().min(1, "Workspace name is required"),
  emails: z.array(z.string()).default([]),
  workspace_ids: z.array(z.number()).default([]),
});

type CreateWorkspaceFormValues = z.infer<typeof createWorkspaceSchema>;

const CreateWorkspaceModalContent = ({
  setOpen,
  organizationId,
}: {
  organizationId: number;
  setOpen: (isOpen: boolean) => void;
}) => {
  const authContext = useAuth();
  const { userToken } = authContext!;
  const createWorkspace = useCreateWorkspace(userToken!);
  const createWorkspaceInvite = useCreateWorkspaceInvite();

  const form = useForm<CreateWorkspaceFormValues>({
    resolver: zodResolver(createWorkspaceSchema),
    defaultValues: { name: "", emails: [], workspace_ids: [] },
  });

  const isSubmitting = createWorkspace.isLoading || createWorkspaceInvite.isLoading;

  const handleSubmit = form.handleSubmit(async (data: CreateWorkspaceFormValues) => {
    if (!organizationId) {
      displayErrorToast("Organization is required to create a workspace.");
      return;
    }

    const workspace = await createWorkspace.mutateAsync({
      name: data.name,
      organization_id: organizationId,
    });

    if (data.emails.length > 0) {
      const response = await createWorkspaceInvite.mutateAsync({
        workspace_id: workspace.id,
        emails: data.emails,
      });
      const successCount = response?.invited?.length ?? 0;
      if (successCount > 0) {
        displayToast(
          successCount === 1
            ? "Successfully sent 1 invitation"
            : `Successfully sent ${successCount} invitations`,
          ToastType.success,
        );
      }
    }

    setOpen(false);
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>Create Workspace</DialogTitle>
        <DialogDescription>Create a new workspace to organize your projects.</DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Workspace name <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g. My Workspace" className="h-[38px]" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <SelectEmails label="Invite members" organizationId={organizationId} required={false} />
          <DialogFooter className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
};

export const CreateWorkspaceModal = ({
  setOpen,
  isOpen,
  organizationId,
  hasTrigger = true,
}: WorkspaceModalProps) => {
  const { data: user } = useCurrentUser();
  const isOrgOwner = user?.memberships.organizations.find(
    org => org.organization_id === organizationId,
  )?.is_owner;
  const { isFreePlan, isPLAdmin } = useOrganizationSubscriptionData();
  const canCreateWorkspace = isPLAdmin || (isOrgOwner && !isFreePlan);

  if (!canCreateWorkspace) {
    return (
      <BasicTooltip
        shouldShow={!isPLAdmin}
        content={
          isFreePlan
            ? "Upgrade to create new workspaces"
            : "You are not the owner of this organization"
        }
      >
        <span className="flex items-center">
          <Button disabled variant="outline">
            <PlusIcon className="mr-2 h-4 w-4" />
            Create Workspace
          </Button>
        </span>
      </BasicTooltip>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      {hasTrigger ? (
        <DialogTrigger asChild>
          <Button variant="outline" onClick={() => setOpen(true)}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Create Workspace
          </Button>
        </DialogTrigger>
      ) : (
        <Button variant="outline" onClick={() => setOpen(true)}>
          <PlusIcon className="mr-2 h-4 w-4" />
          Create Workspace
        </Button>
      )}
      <DialogContent>
        <CreateWorkspaceModalContent setOpen={setOpen} organizationId={organizationId} />
      </DialogContent>
    </Dialog>
  );
};
